import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Icon, Grid, Typography } from '@material-ui/core';
import { LanguageString } from '../../Common/LanguageString';
import { MTS, MTSActivity, MTSActivityType, UserMTSActivityType } from '../../../dto';
import MTSActivityDropDown from '../MTSAddActivity/MTSActivityDropDown';
import { useSaveMTSEntry } from '../../../hooks';
import { MTSActivityInput, MTSUserActivityInput } from 'tuapath-common/generated/schema';
import { format } from 'date-fns';

interface MTSChangeActivityTypeDialogProps {
  open: boolean;
  toggle: () => void;
  mts: MTS;
  type: MTSActivityType | UserMTSActivityType;
  entries: MTSActivity[];
  activityTypes: MTSActivityType[];
  assignedActivityTypeIds?: number[];
  pathId: number;
}

export const MTSChangeActivityTypeDialog: React.FC<MTSChangeActivityTypeDialogProps> = ({ open, toggle, mts, type, entries, activityTypes, assignedActivityTypeIds, pathId }) => {
  const [saveMTSEntry] = useSaveMTSEntry({ refetchQueries: ['mtsEntries', 'mtsActivityType', 'mtsUserActivityType'] });
  const activityButtonRef = useRef<HTMLButtonElement | null>(null);
  const [activityPopoverOpen, setActivityPopoverOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [activityType, setActivityType] = useState<MTSActivityType | UserMTSActivityType>(type);

  useEffect(() => {
    if (open) {
      setActivityType(type);
    }
  }, [open, type]);

  const toggleActivityPopover = () => {
    setActivityPopoverOpen(!activityPopoverOpen);
  };

  const confirmChangeActivityType = async () => {
    if (activityType && activityType.id !== type.id) {
      let overrideUserActivityInput: MTSUserActivityInput | undefined = undefined;

      for (const entry of entries) {
        const parentActivity = 'expiring' in activityType ? activityType.parentActivityType : activityType;

        const date = format(typeof entry.date === 'string' ? new Date(entry.date.replace('00:00:00', '06:00:00')) : entry.date, 'yyyy-MM-dd');

        let userActivityInput: MTSUserActivityInput | undefined = undefined;
        if ('expiring' in activityType) {
          userActivityInput = {
            id: activityType.id,
            parentActivityTypeId: parentActivity.id,
            formSubmissionId: entry.formSubmission?.id ?? 0,
            secondaryFormSubmissionId: entry.secondaryFormSubmission?.id ?? 0
          };
        } else if (activityType.childGeneratedTypesEnabled === true) {
          userActivityInput = {
            parentActivityTypeId: parentActivity.id,
            formSubmissionId: entry.formSubmission?.id ?? 0,
            secondaryFormSubmissionId: entry.secondaryFormSubmission?.id ?? 0
          };
        }

        if (overrideUserActivityInput) {
          userActivityInput = overrideUserActivityInput;
        }

        const input: MTSActivityInput = {
          id: entry.id,
          userId: mts.userId,
          periodId: mts.mtsPeriod?.id,
          date: date,
          hours: entry.hours,
          activityTypeId: parentActivity.id,
          userActivityInput: userActivityInput,
          note: entry.note,
          formSubmissionId: entry.formSubmission?.id,
          secondaryFormSubmissionId: entry.secondaryFormSubmission?.id,
          pathId: pathId
        };

        const result = await saveMTSEntry({
          variables: {
            input: input
          },
          refetchQueries: ['mtsEntries', 'mtsActivityType', 'mtsUserActivityType']
        });

        if (result.data?.saveMTSEntry.userType && userActivityInput) {
          overrideUserActivityInput = {
            ...userActivityInput,
            id: result.data.saveMTSEntry.userType.id
          };
        }
      }
    }

    setConfirmDialogOpen(false);
    toggle();
  };

  return (
    <>
      <Dialog open={open} onClose={toggle} aria-labelledby="change-activity-type-dialog-title" maxWidth="sm" fullWidth>
        <DialogTitle id="change-activity-type-dialog-title">
          <LanguageString groupName="MTS" resourceName="CHANGE_ACTIVITY_TYPE" defaultText="Change Activity Type" />
        </DialogTitle>
        <DialogContent>
          <Button variant="outlined" fullWidth onClick={toggleActivityPopover} ref={activityButtonRef}>
            <Grid container justify="space-between" alignItems="center">
              <Grid item>
                {activityType ? (
                  <Typography>
                    <LanguageString languageString={activityType.name} />
                  </Typography>
                ) : (
                  <LanguageString groupName="MTS" resourceName="ACTIVITY_TYPE" defaultText="Activity Type" />
                )}
              </Grid>
              <Grid item>
                <Icon style={{ height: '100%', display: 'block' }}>expand_more</Icon>
              </Grid>
            </Grid>
          </Button>
          <MTSActivityDropDown
            activityTypes={activityTypes}
            assignedActivityTypeIds={assignedActivityTypeIds ?? []}
            anchor={activityButtonRef}
            open={activityPopoverOpen}
            toggle={toggleActivityPopover}
            existingActivityType={type}
            selectedActivityType={selectedType => {
              setActivityType(selectedType);
            }}
            compatibilityMode={true}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={toggle}>
            <LanguageString groupName="GENERAL" resourceName="CANCEL" defaultText="Cancel" />
          </Button>
          <Button disabled={!activityType || activityType.id === type.id} color="primary" variant="contained" onClick={() => setConfirmDialogOpen(true)}>
            <LanguageString groupName="GENERAL" resourceName="SAVE" defaultText="Save" />
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <LanguageString groupName="GENERAL" resourceName="CONFIRM" defaultText="Confirm" />
        </DialogTitle>
        <DialogContent>
          <Typography>
            <LanguageString
              groupName="MTS"
              resourceName="CHANGE_ACTIVITY_TYPE_CONFIRM"
              defaultText="Are you sure you want to change the activity type to:"
            />
            {activityType && (
              <Typography component="span" style={{fontWeight: 'bold'}}>
                &nbsp;<LanguageString languageString={activityType.name} />
              </Typography>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            <LanguageString groupName="GENERAL" resourceName="NO" defaultText="No" />
          </Button>
          <Button color="primary" variant="contained" onClick={confirmChangeActivityType}>
            <LanguageString groupName="GENERAL" resourceName="YES" defaultText="Yes" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MTSChangeActivityTypeDialog;
